/**
 * Storage keys used for theme preferences
 * These need to be cleared on logout to prevent theme settings from persisting
 * across different user accounts on the same browser
 */
const THEME_STORAGE_KEYS = [
	// Color mode preference (light/dark/system)
	'bonusly-color-mode-preference',
	// Chakra UI's internal color mode key
	'chakra-ui-color-mode',
	// Custom theme preset selection
	'bonusly-custom-theme',
	// User-generated custom themes
	'bonusly-user-themes',
] as const;

/**
 * Seasonal theme storage keys follow a pattern like 'bonusly:seasonal-theme:fall-2025'
 * We need to clear any seasonal theme keys that might exist
 */
const SEASONAL_THEME_KEY_PREFIX = 'bonusly:seasonal-theme:';

/**
 * Clears all theme-related localStorage keys
 *
 * This should be called during logout to prevent theme settings from
 * persisting across different user accounts on the same browser.
 *
 * The operation is wrapped in a try/catch to ensure it doesn't break
 * the logout flow if localStorage is unavailable or throws an error.
 */
export const clearThemeStorage = (): void => {
	try {
		// Clear known theme storage keys
		THEME_STORAGE_KEYS.forEach((key) => {
			localStorage.removeItem(key);
		});

		// Clear any seasonal theme keys
		// We need to iterate through localStorage to find keys matching the pattern
		const keysToRemove: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(SEASONAL_THEME_KEY_PREFIX)) {
				keysToRemove.push(key);
			}
		}
		keysToRemove.forEach((key) => {
			localStorage.removeItem(key);
		});
	} catch (error) {
		// Silently fail - we don't want localStorage issues to break logout
		// The worst case is the next user inherits theme settings, which
		// will be overwritten when they log in and their preferences are loaded
		console.warn('Failed to clear theme storage during logout:', error);
	}
};
